// src/admin/pages/Facebook.jsx
import React, { useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloseIcon from '@mui/icons-material/Close';

// Use the same pattern as the rest of the app:
// PUBLIC_API_BASE -> /admin
const PUBLIC_API_BASE =
    process.env.REACT_APP_PUBLIC_API_BASE || '/api';
const ADMIN_API_BASE = `${PUBLIC_API_BASE}/admin`;
const FACEBOOK_POST_ENDPOINT = `${ADMIN_API_BASE}/facebook/post`;

export default function FacebookAdminPage() {
    const [message, setMessage] = useState('');
    const [media, setMedia] = useState([]); // [{ file, previewUrl }]
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        return () => {
            // cleanup previews on unmount
            media.forEach((m) => {
                if (m.previewUrl) URL.revokeObjectURL(m.previewUrl);
            });
        };
    }, [media]);

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        const mapped = files.map((file) => ({
            file,
            previewUrl: URL.createObjectURL(file),
        }));

        setMedia((prev) => [...prev, ...mapped]);
        event.target.value = '';
    };

    const handleRemoveMedia = (index) => {
        setMedia((prev) => {
            const next = [...prev];
            const [removed] = next.splice(index, 1);
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return next;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        if (!message.trim() && media.length === 0) {
            setError('Please enter a message or attach at least one photo or video.');
            return;
        }

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('message', message.trim());
            media.forEach((m) => {
                formData.append('media', m.file);
            });

            const res = await fetch(FACEBOOK_POST_ENDPOINT, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(
                    data.error ||
                    'Unable to publish to Facebook. Please check the error logs on the server.'
                );
            }

            setSuccess('Successfully posted to the museum Facebook Page.');
            setMessage('');
            media.forEach((m) => {
                if (m.previewUrl) URL.revokeObjectURL(m.previewUrl);
            });
            setMedia([]);
        } catch (err) {
            console.error('Facebook post error:', err);
            setError(err.message || 'Failed to post to Facebook.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {success && (
                <Box sx={{ mb: 2 }}>
                    <Alert
                        severity="success"
                        variant="outlined"
                        onClose={() => setSuccess('')}
                    >
                        {success}
                    </Alert>
                </Box>
            )}
            {error && (
                <Box sx={{ mb: 2 }}>
                    <Alert
                        severity="error"
                        variant="outlined"
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                </Box>
            )}

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Facebook
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Post updates directly to the museum&apos;s Facebook Page. Posts are
                published immediately using the Page account.
            </Typography>

            <Paper sx={{ p: 3 }}>
                <Box component="form" noValidate onSubmit={handleSubmit}>
                    <Stack spacing={2.5}>
                        <TextField
                            label="Post message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                            placeholder="Share an update, event, or announcement..."
                        />

                        <Box>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                            >
                                <Button variant="outlined" component="label">
                                    Add photos or videos
                                    <input
                                        type="file"
                                        accept="image/*,video/*"
                                        multiple
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Button>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: { xs: 1, sm: 0 } }}
                                >
                                    Images and short videos work best. Facebook may take a
                                    moment to process large uploads.
                                </Typography>
                            </Stack>

                            {/* Media previews */}
                            {media.length > 0 && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        p: 1,
                                        maxHeight: 260,
                                        overflowY: 'auto',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1.5,
                                    }}
                                >
                                    {media.map((m, idx) => {
                                        const isVideo = m.file.type.startsWith('video/');
                                        return (
                                            <Box
                                                key={`${m.file.name}-${m.file.size}-${idx}`}
                                                sx={{
                                                    position: 'relative',
                                                    width: 160,
                                                    height: 120,
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    flexShrink: 0,
                                                    bgcolor: 'grey.100',
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        bgcolor: 'rgba(0,0,0,0.6)',
                                                        color: 'common.white',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(0,0,0,0.85)',
                                                        },
                                                    }}
                                                    onClick={() => handleRemoveMedia(idx)}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>

                                                {isVideo ? (
                                                    <Box
                                                        component="video"
                                                        src={m.previewUrl}
                                                        controls
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        component="img"
                                                        src={m.previewUrl}
                                                        alt={m.file.name}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}

                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 6,
                                                        bottom: 4,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        bgcolor: 'rgba(0,0,0,0.55)',
                                                        borderRadius: 999,
                                                        px: 0.75,
                                                        py: 0.25,
                                                        color: 'common.white',
                                                        fontSize: 11,
                                                    }}
                                                >
                                                    {isVideo ? (
                                                        <VideoLibraryIcon
                                                            sx={{ fontSize: 14 }}
                                                        />
                                                    ) : (
                                                        <ImageIcon sx={{ fontSize: 14 }} />
                                                    )}
                                                    <span>
                                                        {m.file.name.length > 16
                                                            ? `${m.file.name.slice(
                                                                0,
                                                                13
                                                            )}…`
                                                            : m.file.name}
                                                    </span>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                endIcon={<SendIcon />}
                                disabled={submitting}
                            >
                                {submitting ? 'Posting…' : 'Post to Facebook'}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}
